import { useEffect, useMemo, useState } from "react";
import { useNavigate } from "react-router-dom";
import { useMutation, useQuery, useQueryClient } from "@tanstack/react-query";
import { supabase } from "@/integrations/supabase/client";
import { useAuth } from "@/hooks/useAuth";
import { useIsAdmin } from "@/hooks/useIsAdmin";
import { useEffectiveBetaMode } from "@/hooks/useEffectiveBetaMode";
import AppHeader from "@/components/AppHeader";
import AppFooter from "@/components/AppFooter";
import { Button } from "@/components/ui/button";
import { Input } from "@/components/ui/input";
import { Badge } from "@/components/ui/badge";
import {
  Table,
  TableBody,
  TableCell,
  TableHead,
  TableHeader,
  TableRow,
} from "@/components/ui/table";
import {
  Select,
  SelectContent,
  SelectItem,
  SelectTrigger,
  SelectValue,
} from "@/components/ui/select";
import {
  AlertDialog,
  AlertDialogAction,
  AlertDialogCancel,
  AlertDialogContent,
  AlertDialogDescription,
  AlertDialogFooter,
  AlertDialogHeader,
  AlertDialogTitle,
} from "@/components/ui/alert-dialog";
import {
  ArrowDown,
  ArrowUp,
  ArrowUpDown,
  ChevronLeft,
  ChevronRight,
  Loader2,
  Megaphone,
  ShieldCheck,
  ShieldOff,
  Trash2,
  Users,
} from "lucide-react";
import { toast } from "sonner";
import { Tabs, TabsContent, TabsList, TabsTrigger } from "@/components/ui/tabs";
import LegalNotifications from "@/components/admin/LegalNotifications";
import EmailCampaignsHistory from "@/components/admin/EmailCampaignsHistory";
import AdminMetrics from "@/components/admin/AdminMetrics";

const PAGE_SIZE = 50;
// "all" | "admins" | `tier:<slug>` — tier filters are derived from DB so adding
// a new subscription_tiers row automatically adds a chip here.
type QuickFilter = "all" | "admins" | `tier:${string}`;

type AdminUser = {
  user_id: string;
  email: string | null;
  display_name: string | null;
  company_name: string | null;
  created_at: string;
  last_sign_in_at: string | null;
  is_admin: boolean;
  subscription_tier_id: string | null;
  tier_name: string | null;
  tier_slug: string | null;
  scheduled_deletion_at: string | null;
  invoices_this_month: number;
};

type SortKey = "email" | "created_at" | "last_sign_in_at" | "tier_name" | "invoices_this_month";


const Admin = () => {
  const navigate = useNavigate();
  const { user, loading: authLoading } = useAuth();
  const { isAdmin, loading: roleLoading } = useIsAdmin();
  const { adminPreviewBeta } = useEffectiveBetaMode();
  const queryClient = useQueryClient();

  const [search, setSearch] = useState("");
  const [sortKey, setSortKey] = useState<SortKey>("created_at");
  const [sortDir, setSortDir] = useState<"asc" | "desc">("desc");
  const [pendingDelete, setPendingDelete] = useState<AdminUser | null>(null);
  const [quickFilter, setQuickFilter] = useState<QuickFilter>("all");
  const [page, setPage] = useState(1);
  const [activeTab, setActiveTab] = useState<"users" | "legal">("users");

  const { data: tiers } = useQuery({
    queryKey: ["all-tiers"],
    enabled: isAdmin,
    queryFn: async () => {
      const { data, error } = await supabase
        .from("subscription_tiers")
        .select("id, name, slug, price_ron")
        .eq("is_active", true)
        .order("sort_order", { ascending: true });
      if (error) throw error;
      return data;
    },
  });

  const { data, isLoading, isFetching } = useQuery({
    queryKey: ["admin-users-list"],
    enabled: isAdmin,
    queryFn: async () => {
      const { data, error } = await supabase.functions.invoke<{ users: AdminUser[] }>(
        "admin-users",
        { method: "GET" },
      );
      if (error) throw error;
      return data?.users ?? [];
    },
  });

  const refresh = () => queryClient.invalidateQueries({ queryKey: ["admin-users-list"] });

  const callAction = async (action: string, payload: Record<string, unknown>) => {
    const { error, data } = await supabase.functions.invoke("admin-users", {
      body: { action, ...payload },
    });
    if (error) throw error;
    if ((data as { error?: string })?.error) throw new Error((data as { error: string }).error);
  };

  const promote = useMutation({
    mutationFn: (u: AdminUser) =>
      callAction(u.is_admin ? "demote" : "promote", { user_id: u.user_id }),
    onSuccess: (_d, u) => {
      toast.success(u.is_admin ? "Admin retras" : "Promovat ca admin");
      refresh();
    },
    onError: (e: Error) => toast.error(e.message),
  });

  const changeTier = useMutation({
    mutationFn: ({ user_id, tier_id }: { user_id: string; tier_id: string }) =>
      callAction("change_tier", { user_id, tier_id }),
    onSuccess: () => {
      toast.success("Tier actualizat");
      refresh();
    },
    onError: (e: Error) => toast.error(e.message),
  });

  const deleteUser = useMutation({
    mutationFn: (u: AdminUser) => callAction("delete", { user_id: u.user_id }),
    onSuccess: () => {
      toast.success("Cont șters");
      setPendingDelete(null);
      refresh();
    },
    onError: (e: Error) => toast.error(e.message),
  });

  const sorted = useMemo(() => {
    const list = [...(data ?? [])];
    const q = search.trim().toLowerCase();
    let filtered = q
      ? list.filter(
          (u) =>
            u.email?.toLowerCase().includes(q) ||
            u.display_name?.toLowerCase().includes(q) ||
            u.company_name?.toLowerCase().includes(q),
        )
      : list;
    if (quickFilter === "admins") filtered = filtered.filter((u) => u.is_admin);
    else if (quickFilter.startsWith("tier:")) {
      const slug = quickFilter.slice(5);
      filtered = filtered.filter((u) => u.tier_slug === slug);
    }
    filtered.sort((a, b) => {
      const av = a[sortKey] as string | number | null;
      const bv = b[sortKey] as string | number | null;
      if (av == null && bv == null) return 0;
      if (av == null) return 1;
      if (bv == null) return -1;
      if (av < bv) return sortDir === "asc" ? -1 : 1;
      if (av > bv) return sortDir === "asc" ? 1 : -1;
      return 0;
    });
    return filtered;
  }, [data, search, sortKey, sortDir, quickFilter]);

  // Reset page whenever filters/search/sort change so user always sees results.
  useEffect(() => setPage(1), [search, quickFilter, sortKey, sortDir]);

  const totalPages = Math.max(1, Math.ceil(sorted.length / PAGE_SIZE));
  const safePage = Math.min(page, totalPages);
  const paged = sorted.slice((safePage - 1) * PAGE_SIZE, safePage * PAGE_SIZE);
  const adminCount = useMemo(() => (data ?? []).filter((u) => u.is_admin).length, [data]);
  const tierCounts = useMemo(() => {
    const m = new Map<string, number>();
    for (const u of data ?? []) {
      if (!u.tier_slug) continue;
      m.set(u.tier_slug, (m.get(u.tier_slug) ?? 0) + 1);
    }
    return m;
  }, [data]);

  const toggleSort = (key: SortKey) => {
    if (sortKey === key) {
      setSortDir(sortDir === "asc" ? "desc" : "asc");
    } else {
      setSortKey(key);
      setSortDir("desc");
    }
  };

  const SortIcon = ({ k }: { k: SortKey }) =>
    sortKey !== k ? (
      <ArrowUpDown className="ml-1 inline h-3 w-3 opacity-40" />
    ) : sortDir === "asc" ? (
      <ArrowUp className="ml-1 inline h-3 w-3" />
    ) : (
      <ArrowDown className="ml-1 inline h-3 w-3" />
    );

  const fmtDate = (s: string | null) => (s ? new Date(s).toLocaleDateString("ro-RO") : "—");

  // Gate after all hooks (no early returns before hooks).
  if (!authLoading && !user) {
    navigate("/auth", { replace: true });
    return null;
  }
  if (!roleLoading && !isAdmin) {
    navigate("/dashboard", { replace: true });
    return null;
  }
  // Block /admin while admin is in "preview Beta" mode — they're simulating a regular user.
  if (isAdmin && adminPreviewBeta) {
    toast.error("Ieși din preview Beta ca să accesezi", {
      description: "Dezactivează butonul „Vezi cum arată în Beta” din header.",
    });
    navigate("/", { replace: true });
    return null;
  }
  if (authLoading || roleLoading) {
    return (
      <div className="flex min-h-screen items-center justify-center">
        <Loader2 className="h-6 w-6 animate-spin text-primary" />
      </div>
    );
  }

  return (
    <div className="flex min-h-screen flex-col bg-background">
      <AppHeader />
      <main className="container flex-1 py-6 space-y-6">
        <div>
          <h1 className="font-heading text-2xl font-bold flex items-center gap-2">
            <ShieldCheck className="h-6 w-6 text-primary" /> Admin
          </h1>
          <p className="text-sm text-muted-foreground">
            Administrează userii și tier-urile.
          </p>
        </div>

        <AdminMetrics />

        <Tabs value={activeTab} onValueChange={(v) => setActiveTab(v as typeof activeTab)} className="space-y-6">
          <TabsList>
            <TabsTrigger value="users" className="gap-2">
              <Users className="h-3.5 w-3.5" />
              Useri
            </TabsTrigger>
            <TabsTrigger value="legal" className="gap-2">
              <Megaphone className="h-3.5 w-3.5" />
              Notificări legale
            </TabsTrigger>
          </TabsList>

          <TabsContent value="users" className="space-y-6">
            <div className="flex items-center justify-between gap-4">
              <p className="text-sm text-muted-foreground">
                {data?.length ?? 0} useri · Promovează admini, schimbă tier-uri, șterge conturi.
              </p>
              <Input
                placeholder="Caută email, nume, companie..."
                value={search}
                onChange={(e) => setSearch(e.target.value)}
                className="max-w-xs"
              />
            </div>

        {/* Quick filter chips — counts use the unfiltered dataset so admins keep context */}
        <div className="flex flex-wrap items-center gap-2">
          <span className="text-xs text-muted-foreground mr-1">Filtre rapide:</span>
          {([
            { key: "all" as QuickFilter, label: `Toți (${data?.length ?? 0})` },
            { key: "admins" as QuickFilter, label: `Doar admini (${adminCount})` },
            ...(tiers ?? []).map((t) => ({
              key: `tier:${t.slug}` as QuickFilter,
              label: `${t.name} (${tierCounts.get(t.slug) ?? 0})`,
            })),
          ]).map((f) => (
            <Button
              key={f.key}
              size="sm"
              variant={quickFilter === f.key ? "default" : "outline"}
              className="h-7 text-xs"
              onClick={() => setQuickFilter(f.key)}
            >
              {f.label}
            </Button>
          ))}
        </div>

        <div className="rounded-xl border border-border bg-card overflow-hidden">
          {isLoading ? (
            <div className="flex items-center justify-center py-16">
              <Loader2 className="h-6 w-6 animate-spin text-primary" />
            </div>
          ) : (
            <Table>
              <TableHeader>
                <TableRow>
                  <TableHead className="cursor-pointer" onClick={() => toggleSort("email")}>
                    User <SortIcon k="email" />
                  </TableHead>
                  <TableHead className="cursor-pointer" onClick={() => toggleSort("tier_name")}>
                    Tier <SortIcon k="tier_name" />
                  </TableHead>
                  <TableHead
                    className="cursor-pointer text-center"
                    onClick={() => toggleSort("invoices_this_month")}
                  >
                    Facturi/lună <SortIcon k="invoices_this_month" />
                  </TableHead>
                  <TableHead className="cursor-pointer" onClick={() => toggleSort("created_at")}>
                    Înregistrat <SortIcon k="created_at" />
                  </TableHead>
                  <TableHead
                    className="cursor-pointer"
                    onClick={() => toggleSort("last_sign_in_at")}
                  >
                    Ultimul login <SortIcon k="last_sign_in_at" />
                  </TableHead>
                  <TableHead className="text-right">Acțiuni</TableHead>
                </TableRow>
              </TableHeader>
              <TableBody>
                {sorted.length === 0 ? (
                  <TableRow>
                    <TableCell colSpan={6} className="text-center text-muted-foreground py-10">
                      Niciun user găsit.
                    </TableCell>
                  </TableRow>
                ) : (
                  paged.map((u) => {
                    const isSelf = u.user_id === user?.id;
                    return (
                      <TableRow key={u.user_id}>
                        <TableCell>
                          <div className="flex flex-col">
                            <div className="flex items-center gap-2">
                              <span className="font-medium">{u.email ?? "—"}</span>
                              {u.is_admin && (
                                <Badge variant="default" className="text-xs">
                                  Admin
                                </Badge>
                              )}
                              {isSelf && (
                                <Badge variant="secondary" className="text-xs">
                                  Tu
                                </Badge>
                              )}
                              {u.scheduled_deletion_at && (
                                <Badge variant="destructive" className="text-xs">
                                  Programat ștergere
                                </Badge>
                              )}
                            </div>
                            <span className="text-xs text-muted-foreground">
                              {u.display_name || u.company_name || "—"}
                            </span>
                          </div>
                        </TableCell>
                        <TableCell>
                          <Select
                            value={u.subscription_tier_id ?? ""}
                            onValueChange={(v) =>
                              changeTier.mutate({ user_id: u.user_id, tier_id: v })
                            }
                          >
                            <SelectTrigger className="h-8 w-[140px] text-xs">
                              <SelectValue placeholder="—" />
                            </SelectTrigger>
                            <SelectContent>
                              {tiers?.map((t) => (
                                <SelectItem key={t.id} value={t.id}>
                                  {t.name}
                                </SelectItem>
                              ))}
                            </SelectContent>
                          </Select>
                        </TableCell>
                        <TableCell className="text-center text-sm">
                          {u.invoices_this_month}
                        </TableCell>
                        <TableCell className="text-sm">{fmtDate(u.created_at)}</TableCell>
                        <TableCell className="text-sm">{fmtDate(u.last_sign_in_at)}</TableCell>
                        <TableCell className="text-right">
                          <div className="flex justify-end gap-1">
                            <Button
                              size="sm"
                              variant="outline"
                              disabled={isSelf || promote.isPending}
                              onClick={() => promote.mutate(u)}
                              title={u.is_admin ? "Retrage admin" : "Promovează admin"}
                            >
                              {u.is_admin ? (
                                <ShieldOff className="h-3.5 w-3.5" />
                              ) : (
                                <ShieldCheck className="h-3.5 w-3.5" />
                              )}
                            </Button>
                            <Button
                              size="sm"
                              variant="outline"
                              className="text-destructive hover:text-destructive"
                              disabled={isSelf}
                              onClick={() => setPendingDelete(u)}
                            >
                              <Trash2 className="h-3.5 w-3.5" />
                            </Button>
                          </div>
                        </TableCell>
                      </TableRow>
                    );
                  })
                )}
              </TableBody>
            </Table>
          )}
          {isFetching && !isLoading && (
            <div className="border-t border-border bg-muted/30 px-4 py-1.5 text-xs text-muted-foreground flex items-center gap-1">
              <Loader2 className="h-3 w-3 animate-spin" /> Se actualizează...
            </div>
          )}
          {!isLoading && sorted.length > 0 && (
            <div className="border-t border-border bg-muted/20 px-4 py-2 flex items-center justify-between text-xs">
              <span className="text-muted-foreground">
                {(safePage - 1) * PAGE_SIZE + 1}–{Math.min(safePage * PAGE_SIZE, sorted.length)} din{" "}
                {sorted.length}
              </span>
              <div className="flex items-center gap-2">
                <Button
                  size="sm"
                  variant="outline"
                  className="h-7 px-2"
                  disabled={safePage <= 1}
                  onClick={() => setPage((p) => Math.max(1, p - 1))}
                >
                  <ChevronLeft className="h-3.5 w-3.5" />
                </Button>
                <span className="text-muted-foreground">
                  Pag. {safePage} / {totalPages}
                </span>
                <Button
                  size="sm"
                  variant="outline"
                  className="h-7 px-2"
                  disabled={safePage >= totalPages}
                  onClick={() => setPage((p) => Math.min(totalPages, p + 1))}
                >
                  <ChevronRight className="h-3.5 w-3.5" />
                </Button>
              </div>
            </div>
          )}
        </div>
          </TabsContent>

          <TabsContent value="legal" className="space-y-6">
            <LegalNotifications />
            <EmailCampaignsHistory />
          </TabsContent>
        </Tabs>
      </main>
      <AppFooter />

      <AlertDialog open={!!pendingDelete} onOpenChange={(o) => !o && setPendingDelete(null)}>
        <AlertDialogContent>
          <AlertDialogHeader>
            <AlertDialogTitle>Șterge contul?</AlertDialogTitle>
            <AlertDialogDescription>
              Vei șterge definitiv <strong>{pendingDelete?.email}</strong> împreună cu profilul,
              facturile și rolurile. Acțiunea nu poate fi anulată.
            </AlertDialogDescription>
          </AlertDialogHeader>
          <AlertDialogFooter>
            <AlertDialogCancel>Anulează</AlertDialogCancel>
            <AlertDialogAction
              className="bg-destructive text-destructive-foreground hover:bg-destructive/90"
              onClick={() => pendingDelete && deleteUser.mutate(pendingDelete)}
            >
              {deleteUser.isPending ? (
                <Loader2 className="h-4 w-4 animate-spin" />
              ) : (
                "Șterge definitiv"
              )}
            </AlertDialogAction>
          </AlertDialogFooter>
        </AlertDialogContent>
      </AlertDialog>
    </div>
  );
};

export default Admin;
